import React, { useEffect, useRef, useState } from "react";
import { GetCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import Select from "react-select";
import Spinner from "utils/Spinner";
import { Button, Card, CardBody, Chip, Popover, PopoverTrigger, PopoverContent, Checkbox } from "@heroui/react";
import {
  CalendarDaysIcon,
  PlusIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  PaperClipIcon,
  DocumentArrowDownIcon,
  UserIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";
import { utils, write } from "xlsx";
import { saveAs } from "file-saver";
import ApplyLeaveModal from "pages/Leave/ApplyLeaveModal";
import { roles } from "utils/constant";
import { AfDateRangePicker } from "components/Input/AfDateRangePicker";

const pickupSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.2)" : "none",
    padding: "0px 4px",
    minHeight: "36px",
    fontSize: "13px",
    transition: "all 0.2s ease",
    "&:hover": { borderColor: "#0077B6" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 8px" }),
  input: (base: any) => ({ ...base, margin: "0px", padding: "0px" }),
  indicatorsContainer: (base: any) => ({ ...base, height: "36px" }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected ? "#0077B6" : state.isFocused ? "#e0f2fe" : "white",
    color: state.isSelected ? "white" : "#1e293b",
    fontSize: "13px",
    padding: "6px 12px",
    cursor: "pointer",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
    zIndex: 99999,
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
  placeholder: (base: any) => ({ ...base, color: "#94a3b8", fontSize: "13px" }),
  singleValue: (base: any) => ({ ...base, color: "#1e293b", fontSize: "13px", fontWeight: "500" }),
};

const getFinancialYear = (date?: any) => {
  const m = date ? moment(date) : moment();
  const year = m.year();
  const month = m.month(); // 0-indexed, April is 3
  if (month >= 3) {
    return `${year}-${year + 1}`;
  } else {
    return `${year - 1}-${year}`;
  }
};

const getFinancialYearOptions = () => {
  const currentFY = getFinancialYear();
  const currentStartYear = parseInt(currentFY.split("-")[0]);
  return [
    { value: `${currentStartYear + 1}-${currentStartYear + 2}`, label: `FY ${currentStartYear + 1}-${currentStartYear + 2}` },
    { value: `${currentStartYear}-${currentStartYear + 1}`, label: `FY ${currentStartYear}-${currentStartYear + 1} (Current)` },
    { value: `${currentStartYear - 1}-${currentStartYear}`, label: `FY ${currentStartYear - 1}-${currentStartYear}` },
    { value: `${currentStartYear - 2}-${currentStartYear - 1}`, label: `FY ${currentStartYear - 2}-${currentStartYear - 1}` },
  ];
};

const LeaveReport: React.FC = () => {
  const userD = (() => {
    const auth = localStorage.getItem("auth");
    return auth ? JSON.parse(auth) : {};
  })();
  const userDetails = userD?.state?.user || {};
  const isSuperAdmin = (() => {
    const roleName = String(userDetails.roleName || "").trim();
    const roleId = Number(userDetails.role_name || userDetails.role || 0);
    const username = String(userDetails.username || "").trim().toUpperCase();
    return (
      roleName === roles.SUPER_ADMIN || // "Admin"
      roleName.toUpperCase() === "ADMIN" ||
      roleName.toUpperCase() === "SUPER_ADMIN" ||
      roleName.toUpperCase() === "SUPER ADMIN" ||
      roleId === 1 ||
      username === "ADMIN" ||
      username === "AKASH"
    );
  })();

  const [activeTab, setActiveTab] = useState<"MY_LEAVES" | "APPROVALS">(
    isSuperAdmin ? "APPROVALS" : "MY_LEAVES"
  );
  const effectiveTab = isSuperAdmin ? activeTab : "MY_LEAVES";

  const [selectedFY, setSelectedFY] = useState<any>({
    value: getFinancialYear(),
    label: `FY ${getFinancialYear()} (Current)`,
  });

  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [typeFilter, setTypeFilter] = useState<string>("ALL");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>(moment().add(1, 'M').endOf('month').format('YYYY-MM-DD'));

  // Admin filter states
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userOptions, setUserOptions] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [branchOptions, setBranchOptions] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const perPage = 20;

  // Modals state
  const [isApplyModalOpen, setIsApplyModalOpen] = useState<boolean>(false);
  const [editApplicationData, setEditApplicationData] = useState<any>(null);
  const [detailModalItem, setDetailModalItem] = useState<any>(null);
  const [applicantQuotaInfo, setApplicantQuotaInfo] = useState<any>(null);

  const tableRef = useRef<HTMLTableElement>(null);

  // Fetch Users for Filter (Super Admin)
  useQuery(
    ["userListForLeaveReport"],
    () => ListCommanWay("/admin/user/list", { isActive: true }, { paginate: 1000 }),
    {
      enabled: isSuperAdmin,
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          const list = res?.data?.data || res?.data?.docs || [];
          setUserOptions(
            list.map((u: any) => ({
              value: u.id,
              label: `${u.name || u.username} (${u.email || u.mobile || "#" + u.id})`,
            }))
          );
        }
      },
    }
  );

  // Fetch Branches for Filter (Super Admin)
  useQuery(
    ["subbranchListForLeaveReport"],
    () => ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 }),
    {
      enabled: isSuperAdmin,
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          const list = res?.data?.data || res?.data?.docs || [];
          setBranchOptions(list.map((b: any) => ({ value: b.id, label: b.branch_name || b.name || `#${b.id}` })));
        }
      },
    }
  );

  // Fetch My Summary & Quota
  const {
    data: summaryData,
    isLoading: isLoadingSummary,
    refetch: refetchSummary,
  } = useQuery(
    ["myLeaveSummaryReport", selectedFY?.value],
    () => GetCommanWay(`/admin/leave-application/my-summary?financialYear=${selectedFY?.value}`, {}, {}),
    {
      keepPreviousData: true,
    }
  );

  const summary = summaryData?.data || {};
  const quota = summary?.quota || {};

  // Build Query for List
  const buildQuery = () => {
    const q: any = {};
    if (selectedFY?.value) q.financialYear = selectedFY.value;
    if (typeFilter && typeFilter !== "ALL") q.leaveType = typeFilter;

    // Date range filter conditions
    const dateCond: any = {};
    if (startDate) dateCond.fromDate = { $gte: startDate };
    if (endDate) dateCond.toDate = { $lte: endDate };
    const hasDateFilter = Object.keys(dateCond).length > 0;

    if (statusFilter === "PENDING") {
      // In application list show all PENDING applications no matter date
      q.status = "PENDING";
    } else if (statusFilter && statusFilter !== "ALL") {
      // Specific non-pending status (APPROVED, REJECTED, CANCELLED) -> apply date filter
      q.status = statusFilter;
      if (hasDateFilter) {
        if (dateCond.fromDate) q.fromDate = dateCond.fromDate;
        if (dateCond.toDate) q.toDate = dateCond.toDate;
      }
    } else {
      // statusFilter === "ALL":
      // Show all PENDING applications no matter date, plus other applications in selected date range
      if (hasDateFilter) {
        q.$or = [
          { status: "PENDING" },
          dateCond,
        ];
      }
    }

    if (effectiveTab === "MY_LEAVES") {
      const currentUserId = userDetails?.id || userDetails?.user_id;
      if (currentUserId && isSuperAdmin) {
        q.user = currentUserId;
      }
    } else {
      // In Approval Queue (All Applications)
      if (selectedUser?.value) q.user = selectedUser.value;
      if (selectedBranch?.value) {
        q.subbranch = selectedBranch.value;
      }
    }
    return q;
  };

  // Fetch Applications List
  const {
    data: applicationsData,
    isLoading: isLoadingList,
    isFetching: isFetchingList,
    refetch: refetchList,
  } = useQuery(
    ["leaveReportList", effectiveTab, userDetails?.id, selectedFY?.value, statusFilter, typeFilter, startDate, endDate, selectedUser?.value, selectedBranch?.value, currentPage],
    () =>
      ListCommanWay(
        "/admin/leave-application/list",
        buildQuery(),
        { page: currentPage, paginate: perPage, sort: { createdAt: -1 } }
      ),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: true,
    }
  );

  const getApplicationsList = (): any[] => {
    let list: any[] = [];
    if (Array.isArray(applicationsData?.data?.data)) {
      list = applicationsData.data.data;
    } else if (Array.isArray(applicationsData?.data?.docs)) {
      list = applicationsData.data.docs;
    } else if (Array.isArray(applicationsData?.data)) {
      list = applicationsData.data;
    }

    if (list.length === 0 && Array.isArray(summary?.recentApplications) && summary.recentApplications.length > 0) {
      if (effectiveTab === "MY_LEAVES") {
        list = summary.recentApplications;
      }
    }
    return list;
  };

  const applicationsList: any[] = getApplicationsList();

  const paginator = applicationsData?.data?.paginator || {
    itemCount: applicationsList.length,
    pageCount: Math.ceil(applicationsList.length / perPage) || 1,
    currentPage: 1,
  };

  // Fetch Applicant's Quota when reviewing in detail modal
  useQuery(
    ["applicantQuotaForReview", detailModalItem?.user?.id || detailModalItem?.user, selectedFY?.value],
    () => {
      const uId = detailModalItem?.user?.id || detailModalItem?.user;
      return GetCommanWay(`/admin/leave-quota/user/${uId}?financialYear=${selectedFY?.value}`, {}, {});
    },
    {
      enabled: !!detailModalItem?.user && isSuperAdmin,
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          setApplicantQuotaInfo(res?.data?.quota || res?.data);
        }
      },
    }
  );

  const handleRefreshAll = () => {
    refetchSummary();
    refetchList();
  };

  // Action Mutation (Approve / Reject)
  const { mutate: performAction, isLoading: isActionLoading } = useMutation(
    ({ id, status, actionRemarks }: { id: number; status: "APPROVED" | "REJECTED"; actionRemarks?: string }) =>
      UpdateCommanWay({
        url: `/admin/leave-application/action/${id}`,
        data: { status, actionRemarks },
      }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          UseToast(`Leave application ${res?.data?.status || "action processed"} successfully!`, "success");
          handleRefreshAll();
          setDetailModalItem(null);
        } else {
          UseToast(res?.message || "Failed to process action", "error");
        }
      },
      onError: (err: any) => {
        UseToast(err?.message || "Error processing action", "error");
      },
    }
  );

  // Cancel Mutation (for user or admin)
  const { mutate: cancelApplication, isLoading: isCancelling } = useMutation(
    ({ id, reason }: { id: number; reason: string }) =>
      UpdateCommanWay({
        url: `/admin/leave-application/cancel/${id}`,
        data: { reason },
      }),
    {
      onSuccess: (res: any) => {
        if (res?.status === "SUCCESS") {
          UseToast("Leave application cancelled. Quota restored if applicable.", "success");
          handleRefreshAll();
          setDetailModalItem(null);
        } else {
          UseToast(res?.message || "Failed to cancel application", "error");
        }
      },
      onError: (err: any) => {
        UseToast(err?.message || "Error cancelling leave", "error");
      },
    }
  );

  const handleApprove = (item: any) => {
    if (!isSuperAdmin) {
      UseToast("Only Super Admin can approve leave applications.", "error");
      return;
    }
    Swal.fire({
      title: "Approve Leave Application?",
      html: `
        <div class="text-left text-sm space-y-1 text-slate-700">
          <p><strong>Employee:</strong> ${item._user?.name || item.user_data?.name || item._user?.username || item.user_data?.username || "Employee #" + item.user}</p>
          <p><strong>Leave Type:</strong> ${item.leaveType}</p>
          <p><strong>Duration:</strong> ${item.totalDays} Day(s) (${moment(item.fromDate).format("DD MMM")} - ${moment(item.toDate).format("DD MMM")})</p>
          <p class="text-emerald-700 font-semibold mt-2">Days will be automatically deducted from user balance.</p>
        </div>
      `,
      icon: "question",
      input: "text",
      inputLabel: "Approval Remarks (Optional):",
      inputPlaceholder: "e.g., Approved as discussed",
      showCancelButton: true,
      confirmButtonColor: "#059669",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Confirm Approval",
    }).then((res) => {
      if (res.isConfirmed) {
        performAction({
          id: item.id,
          status: "APPROVED",
          actionRemarks: res.value || "Approved by Super Admin",
        });
      }
    });
  };

  const handleReject = (item: any) => {
    if (!isSuperAdmin) {
      UseToast("Only Super Admin can reject leave applications.", "error");
      return;
    }
    Swal.fire({
      title: "Reject Leave Application?",
      text: "Please provide reason/remarks for rejection. No quota will be deducted.",
      icon: "warning",
      input: "textarea",
      inputLabel: "Rejection Reason (Required):",
      inputPlaceholder: "Enter justification...",
      inputValidator: (value) => {
        if (!value || !value.trim()) {
          return "Rejection remarks are required!";
        }
      },
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Reject Application",
    }).then((res) => {
      if (res.isConfirmed) {
        performAction({
          id: item.id,
          status: "REJECTED",
          actionRemarks: res.value.trim(),
        });
      }
    });
  };

  const handleCancelClick = (item: any) => {
    const isOwner = Number(item.user) === Number(userDetails?.id || userDetails?.user_id);
    if (!isOwner && !isSuperAdmin) {
      UseToast("You do not have permission to cancel this application.", "error");
      return;
    }
    Swal.fire({
      title: "Cancel Leave Application?",
      text: item.status === "APPROVED"
        ? "This leave was APPROVED. Cancelling will automatically restore the days back to available balance."
        : "Are you sure you want to cancel this pending application?",
      icon: "warning",
      input: "textarea",
      inputLabel: "Reason for cancellation (optional):",
      inputPlaceholder: "Enter reason...",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Cancel Leave",
    }).then((result) => {
      if (result.isConfirmed) {
        cancelApplication({ id: item.id, reason: result.value || "Cancelled by applicant" });
      }
    });
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!tableRef.current) return;
    const tableClone = tableRef.current.cloneNode(true) as HTMLTableElement;
    const toIgnore = tableClone.querySelectorAll(".ignore-excel");
    toIgnore.forEach((el) => el.remove());

    const ws = utils.table_to_sheet(tableClone);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Leave Applications");
    const wbout = write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `Leave_Report_${selectedFY?.value}.xlsx`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "PENDING":
        return <Chip size="sm" variant="flat" color="warning" className="text-xs font-bold" startContent={<ClockIcon className="w-4 h-4" />}>Pending</Chip>;
      case "APPROVED":
        return <Chip size="sm" variant="flat" color="success" className="text-xs font-bold" startContent={<CheckCircleIcon className="w-4 h-4" />}>Approved</Chip>;
      case "REJECTED":
        return <Chip size="sm" variant="flat" color="danger" className="text-xs font-bold" startContent={<XCircleIcon className="w-4 h-4" />}>Rejected</Chip>;
      case "CANCELLED":
        return <Chip size="sm" variant="flat" color="default" className="text-xs font-semibold" startContent={<NoSymbolIcon className="w-4 h-4 text-slate-500" />}>Cancelled</Chip>;
      default:
        return <Chip size="sm" variant="flat">{status}</Chip>;
    }
  };

  const getLeaveTypeBadge = (type: string) => {
    switch (type) {
      case "CASUAL":
        return <span className="px-2.5 py-1 bg-blue-50 text-blue-800 border border-blue-200 rounded-lg text-xs font-bold">Casual (CL)</span>;
      case "SICK":
        return <span className="px-2.5 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg text-xs font-bold">Sick (SL)</span>;
      case "PAID":
        return <span className="px-2.5 py-1 bg-purple-50 text-purple-800 border border-purple-200 rounded-lg text-xs font-bold">Paid (PL)</span>;
      case "COMPENSATORY":
        return <span className="px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200 rounded-lg text-xs font-bold">Comp-Off</span>;
      case "MATERNITY":
        return <span className="px-2.5 py-1 bg-pink-50 text-pink-800 border border-pink-200 rounded-lg text-xs font-bold">Maternity</span>;
      case "UNPAID":
        return <span className="px-2.5 py-1 bg-red-50 text-red-800 border border-red-200 rounded-lg text-xs font-bold">Unpaid / LWP</span>;
      default:
        return <span className="px-2.5 py-1 bg-slate-100 text-slate-800 border border-slate-200 rounded-lg text-xs font-bold">{type}</span>;
    }
  };

  return (
    <div className="bg-gradient-to-br from-purple-400 via-blue-50 to-primary/50 py-2 px-2 font-inter min-h-screen">
      {(isLoadingList || isFetchingList || isLoadingSummary) && <Spinner />}

      {/* 1. Header Bar */}
      <div className="mx-auto mb-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-3 py-2 shadow-xl shadow-slate-200/50">
          <div className="flex items-center justify-between">
            {/* Total on Left */}
            <div className="flex items-center gap-2">
              <div className="bg-green-100 text-green-700 px-3 py-1 rounded-lg text-sm font-semibold">
                Paid Leaves: {quota?.totalRemainingPaidLeaves ?? (quota?.casualRemaining || 0) + (quota?.sickRemaining || 0) + (quota?.paidRemaining || 0) + (quota?.compOffRemaining || 0)} Days
              </div>

              {summary?.pendingApplicationsCount > 0 && (
                <div className="bg-amber-100 text-amber-800 px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5">
                  <ClockIcon className="w-4 h-4 text-amber-600" />
                  <span>{summary.pendingApplicationsCount} Pending</span>
                </div>
              )}

              {quota?.isProrated && (
                <div className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-lg text-xs font-semibold">
                  Prorated
                </div>
              )}
            </div>

            {/* Centered Gradient Title */}
            <h1 className="text-base font-bold bg-gradient-to-r from-primary to-amber-600 bg-clip-text text-transparent uppercase">
              LEAVE REPORT
            </h1>

            {/* FY Selector & Apply Button on Right */}
            <div className="flex items-center gap-2">
              <div className="w-44">
                <Select
                  options={getFinancialYearOptions()}
                  value={selectedFY}
                  onChange={setSelectedFY}
                  styles={pickupSelectStyles}
                  isSearchable={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>

              <Button
                size="sm"
                variant="bordered"
                isIconOnly
                onPress={handleRefreshAll}
                className="h-8 w-8 bg-white/90 rounded-lg text-slate-700"
                title="Refresh"
              >
                <ArrowPathIcon className="w-4 h-4" />
              </Button>

              <Button
                size="sm"
                color="primary"
                onPress={() => {
                  setEditApplicationData(null);
                  setIsApplyModalOpen(true);
                }}
                startContent={<PlusIcon className="w-4 h-4" />}
                className="h-8 px-3 text-xs font-semibold rounded-lg bg-primary text-white shadow hover:bg-primary/90"
              >
                Apply Leave
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Leave Balance Cards Bar */}
      <div className="mx-auto mb-3">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-xl shadow-slate-200/50">
          <div className="grid grid-cols-6 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-3">
            {/* Casual Leave */}
            <div className="bg-white/95 border border-blue-100 rounded-xl p-3.5 shadow-2xs hover:border-blue-300 transition-all">
              <div className="flex items-center justify-between text-xs font-bold text-blue-900 mb-1.5">
                <span>CASUAL (CL)</span>
                <span className="text-xs text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md font-semibold">Total: {quota?.casualTotal ?? 0}</span>
              </div>
              <div className="flex items-baseline gap-1.5 my-1">
                <span className="text-2xl md:text-3xl font-extrabold text-slate-900">{quota?.casualRemaining ?? 0}</span>
                <span className="text-xs font-medium text-slate-500">/ {quota?.casualTotal ?? 0} Days</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-600 mt-2 pt-1.5 border-t border-slate-100">
                <span>Used: <strong className="text-slate-800">{quota?.casualUsed ?? 0}</strong></span>
                <div className="w-16 bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-blue-600 h-full rounded-full"
                    style={{ width: `${Math.min(100, Math.max(0, ((quota?.casualRemaining || 0) / (quota?.casualTotal || 1)) * 100))}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Sick Leave */}
            <div className="bg-white/95 border border-emerald-100 rounded-xl p-3.5 shadow-2xs hover:border-emerald-300 transition-all">
              <div className="flex items-center justify-between text-xs font-bold text-emerald-900 mb-1.5">
                <span>SICK (SL)</span>
                <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md font-semibold">Total: {quota?.sickTotal ?? 0}</span>
              </div>
              <div className="flex items-baseline gap-1.5 my-1">
                <span className="text-2xl md:text-3xl font-extrabold text-slate-900">{quota?.sickRemaining ?? 0}</span>
                <span className="text-xs font-medium text-slate-500">/ {quota?.sickTotal ?? 0} Days</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-600 mt-2 pt-1.5 border-t border-slate-100">
                <span>Used: <strong className="text-slate-800">{quota?.sickUsed ?? 0}</strong></span>
                <div className="w-16 bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-emerald-600 h-full rounded-full"
                    style={{ width: `${Math.min(100, Math.max(0, ((quota?.sickRemaining || 0) / (quota?.sickTotal || 1)) * 100))}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Paid Leave */}
            <div className="bg-white/95 border border-purple-100 rounded-xl p-3.5 shadow-2xs hover:border-purple-300 transition-all">
              <div className="flex items-center justify-between text-xs font-bold text-purple-900 mb-1.5">
                <span>PAID (PL)</span>
                <span className="text-xs text-purple-700 bg-purple-50 px-2 py-0.5 rounded-md font-semibold">Total: {quota?.paidTotal ?? 0}</span>
              </div>
              <div className="flex items-baseline gap-1.5 my-1">
                <span className="text-2xl md:text-3xl font-extrabold text-slate-900">{quota?.paidRemaining ?? 0}</span>
                <span className="text-xs font-medium text-slate-500">/ {quota?.paidTotal ?? 0} Days</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-600 mt-2 pt-1.5 border-t border-slate-100">
                <span>Used: <strong className="text-slate-800">{quota?.paidUsed ?? 0}</strong></span>
                <div className="w-16 bg-slate-100 h-2 rounded-full overflow-hidden">
                  <div
                    className="bg-purple-600 h-full rounded-full"
                    style={{ width: `${Math.min(100, Math.max(0, ((quota?.paidRemaining || 0) / (quota?.paidTotal || 1)) * 100))}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Comp-Off */}
            <div className="bg-white/95 border border-amber-100 rounded-xl p-3.5 shadow-2xs hover:border-amber-300 transition-all">
              <div className="flex items-center justify-between text-xs font-bold text-amber-900 mb-1.5">
                <span>COMP-OFF</span>
                <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md font-semibold">
                  {quota?.isCompOffEligible ? `Earned: ${quota?.compOffEarned ?? 0}` : "N/A"}
                </span>
              </div>
              <div className="flex items-baseline gap-1.5 my-1">
                <span className="text-2xl md:text-3xl font-extrabold text-slate-900">
                  {quota?.isCompOffEligible ? quota?.compOffRemaining ?? 0 : 0}
                </span>
                <span className="text-xs font-medium text-slate-500">/ {quota?.compOffEarned ?? 0} Days</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-600 mt-2 pt-1.5 border-t border-slate-100">
                <span>Used: <strong className="text-slate-800">{quota?.compOffUsed ?? 0}</strong></span>
                <span className="text-xs text-amber-800 font-bold">{quota?.isCompOffEligible ? "Eligible" : "Not Active"}</span>
              </div>
            </div>

            {/* Maternity */}
            <div className="bg-white/95 border border-pink-100 rounded-xl p-3.5 shadow-2xs hover:border-pink-300 transition-all">
              <div className="flex items-center justify-between text-xs font-bold text-pink-900 mb-1.5">
                <span>MATERNITY</span>
                <span className="text-xs text-pink-700 bg-pink-50 px-2 py-0.5 rounded-md font-semibold">
                  {quota?.isMaternityEligible ? `Total: ${quota?.maternityTotal ?? 0}` : "N/A"}
                </span>
              </div>
              <div className="flex items-baseline gap-1.5 my-1">
                <span className="text-2xl md:text-3xl font-extrabold text-slate-900">
                  {quota?.isMaternityEligible ? quota?.maternityRemaining ?? 0 : 0}
                </span>
                <span className="text-xs font-medium text-slate-500">/ {quota?.maternityTotal ?? 0} Days</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-600 mt-2 pt-1.5 border-t border-slate-100">
                <span>Used: <strong className="text-slate-800">{quota?.maternityUsed ?? 0}</strong></span>
                <span className="text-xs text-pink-800 font-bold">{quota?.isMaternityEligible ? "Eligible" : "Not Active"}</span>
              </div>
            </div>

            {/* Unpaid / LWP */}
            <div className="bg-white/95 border border-red-100 rounded-xl p-3.5 shadow-2xs hover:border-red-300 transition-all">
              <div className="flex items-center justify-between text-xs font-bold text-red-900 mb-1.5">
                <span>UNPAID (LWP)</span>
                <span className="text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded-md font-semibold">Salary Deduct</span>
              </div>
              <div className="flex items-baseline gap-1.5 my-1">
                <span className="text-2xl md:text-3xl font-extrabold text-red-700">{quota?.unpaidUsed ?? 0}</span>
                <span className="text-xs font-medium text-slate-500">Days Taken</span>
              </div>
              <div className="flex items-center justify-between text-xs text-slate-600 mt-2 pt-1.5 border-t border-slate-100">
                <span>Total Consumed: <strong className="text-slate-800">{quota?.unpaidUsed ?? 0}</strong></span>
                <span className="text-[11px] text-slate-400 font-medium">LWP</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Filters & View Switcher Card */}
      <div className="mx-auto mb-3">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-xl shadow-slate-200/50">
          <div className="flex flex-wrap items-end justify-between gap-4">
            {/* View Switcher Tabs (My Leaves vs Approval Queue for SuperAdmin) */}
            <div className="flex items-center gap-3">
              {isSuperAdmin ? (
                <div className="flex items-center bg-slate-100 p-1.5 rounded-xl gap-1.5 text-sm">
                  <button
                    onClick={() => {
                      setActiveTab("MY_LEAVES");
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg font-bold transition-all ${
                      effectiveTab === "MY_LEAVES"
                        ? "bg-white text-primary shadow-sm"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    My Leaves
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab("APPROVALS");
                      setCurrentPage(1);
                    }}
                    className={`px-4 py-2 rounded-lg font-bold transition-all flex items-center gap-2 ${
                      effectiveTab === "APPROVALS"
                        ? "bg-primary text-white shadow-sm"
                        : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    <span>Approval Queue</span>
                    {summary?.pendingApplicationsCount > 0 && (
                      <span className="bg-amber-400 text-slate-950 text-xs font-extrabold px-2 py-0.5 rounded-full">
                        {summary.pendingApplicationsCount}
                      </span>
                    )}
                  </button>
                </div>
              ) : (
                <span className="text-sm font-extrabold text-slate-800">My Leave Applications</span>
              )}

              {/* Status Filter Chips */}
              <div className="flex items-center bg-slate-100/90 p-1 rounded-xl gap-1 text-xs">
                {["ALL", "PENDING", "APPROVED", "REJECTED", "CANCELLED"].map((st) => (
                  <button
                    key={st}
                    onClick={() => {
                      setStatusFilter(st);
                      setCurrentPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-lg font-bold transition-all ${
                      statusFilter === st
                        ? "bg-white text-slate-900 shadow-xs"
                        : "text-slate-500 hover:text-slate-900"
                    }`}
                  >
                    {st === "ALL" ? "All" : st.charAt(0) + st.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Filter Inputs Row */}
            <div className="flex flex-wrap items-end gap-3">
              {/* Date Range */}
              <div className="w-64">
                <AfDateRangePicker
                  label="Date Range"
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={(d: any) => {
                    setStartDate(d);
                    setCurrentPage(1);
                  }}
                  setEndDate={(d: any) => {
                    setEndDate(d);
                    setCurrentPage(1);
                  }}
                />
              </div>

              {/* Leave Type Select */}
              <div className="w-40">
                <label className="block mb-1 text-xs font-semibold text-slate-700">Leave Type</label>
                <Select
                  options={[
                    { value: "ALL", label: "All Types" },
                    { value: "CASUAL", label: "Casual (CL)" },
                    { value: "SICK", label: "Sick (SL)" },
                    { value: "PAID", label: "Paid (PL)" },
                    { value: "COMPENSATORY", label: "Comp-Off" },
                    { value: "MATERNITY", label: "Maternity" },
                    { value: "UNPAID", label: "Unpaid / LWP" },
                  ]}
                  value={{ value: typeFilter, label: typeFilter === "ALL" ? "All Types" : typeFilter }}
                  onChange={(e: any) => {
                    setTypeFilter(e?.value || "ALL");
                    setCurrentPage(1);
                  }}
                  styles={pickupSelectStyles}
                  isSearchable={false}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>

              {/* Super Admin Filters in Approvals Tab */}
              {effectiveTab === "APPROVALS" && isSuperAdmin && (
                <>
                  <div className="w-52">
                    <label className="block mb-1 text-xs font-semibold text-slate-700">Employee</label>
                    <Select
                      options={[{ value: null, label: "All Employees" }, ...userOptions]}
                      value={selectedUser}
                      onChange={(e) => {
                        setSelectedUser(e);
                        setCurrentPage(1);
                      }}
                      placeholder="Select Employee"
                      isClearable
                      styles={pickupSelectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>

                  <div className="w-44">
                    <label className="block mb-1 text-xs font-semibold text-slate-700">Branch</label>
                    <Select
                      options={[{ value: null, label: "All Branches" }, ...branchOptions]}
                      value={selectedBranch}
                      onChange={(e) => {
                        setSelectedBranch(e);
                        setCurrentPage(1);
                      }}
                      placeholder="Select Branch"
                      isClearable
                      styles={pickupSelectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                </>
              )}

              {/* Export Button */}
              <Button
                size="sm"
                variant="flat"
                color="primary"
                className="h-9 px-4 rounded-xl text-xs font-bold"
                startContent={<DocumentArrowDownIcon className="w-4 h-4" />}
                onPress={exportToExcel}
              >
                Export
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Table Container */}
      <div className="mx-auto mb-3">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl shadow-slate-200/50 p-4 overflow-hidden">
          {applicationsList.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <CalendarDaysIcon className="w-14 h-14 mx-auto text-slate-300 mb-2 stroke-1" />
              <p className="text-base font-semibold text-slate-600">
                {effectiveTab === "MY_LEAVES" ? "No personal leave applications found" : "No applications in Approval Queue"}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {effectiveTab === "MY_LEAVES" && isSuperAdmin
                  ? "Looking to approve employee leaves? Switch to the Approval Queue tab above."
                  : "Try changing the status filter or date range."}
              </p>
              {effectiveTab === "MY_LEAVES" && isSuperAdmin && (
                <Button
                  size="sm"
                  color="primary"
                  variant="flat"
                  onPress={() => setActiveTab("APPROVALS")}
                  className="mt-3 text-xs font-bold"
                >
                  View Approval Queue ({summary?.pendingApplicationsCount || 0})
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto max-h-[65vh]">
              <table ref={tableRef} className="w-full text-left text-sm">
                <thead className="bg-slate-100/90 text-slate-800 font-bold uppercase tracking-wider text-xs sticky top-0 z-10 backdrop-blur-sm border-b border-slate-200">
                  <tr>
                    {effectiveTab === "APPROVALS" && <th className="py-3 px-4">Employee</th>}
                    <th className="py-3 px-4">Leave Type</th>
                    <th className="py-3 px-4">Duration & Dates</th>
                    <th className="py-3 px-4">Reason</th>
                    <th className="py-3 px-4">Applied On</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right ignore-excel">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-800">
                  {applicationsList.map((item: any) => {
                    const applicantName = item._user?.name || item.user_data?.name || item._user?.username || item.user_data?.username || `User #${item.user}`;
                    const applicantBranch = item._user?._subbranch?.branch_name || item._user?._branch?.name || item._user?.branch_name || item.user_data?.subbranch_data?.branch_name || item.user_data?.branch || "—";
                    return (
                      <tr key={item.id} className="hover:bg-blue-50/50 transition-colors">
                        {effectiveTab === "APPROVALS" && (
                          <td className="py-3 px-4 whitespace-nowrap">
                            <div className="font-bold text-slate-900 flex items-center gap-1.5 text-sm">
                              <UserIcon className="w-4 h-4 text-slate-400" />
                              <span>{applicantName}</span>
                            </div>
                            <div className="text-xs text-slate-500 font-medium">{applicantBranch}</div>
                          </td>
                        )}

                        <td className="py-3 px-4 whitespace-nowrap">
                          {getLeaveTypeBadge(item.leaveType)}
                        </td>

                        <td className="py-3 px-4 whitespace-nowrap">
                          <div className="font-bold text-slate-900 text-sm">
                            {item.totalDays} {item.totalDays === 1 ? "Day" : "Days"}
                            {item.isHalfDay && (
                              <span className="ml-1.5 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-bold">
                                {item.halfDaySession === "FIRST_HALF" ? "1st Half" : "2nd Half"}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 font-medium mt-0.5">
                            {moment(item.fromDate).format("DD MMM YYYY")}
                            {item.toDate && item.toDate !== item.fromDate && ` → ${moment(item.toDate).format("DD MMM YYYY")}`}
                          </div>
                        </td>

                        <td className="py-3 px-4 max-w-xs">
                          <p className="truncate text-slate-800 text-sm" title={item.reason}>{item.reason || "—"}</p>
                          {item.actionRemarks && (
                            <p className="text-xs text-slate-500 italic mt-0.5 truncate" title={item.actionRemarks}>
                              Remarks: {item.actionRemarks}
                            </p>
                          )}
                        </td>

                        <td className="py-3 px-4 text-slate-600 whitespace-nowrap text-xs font-medium">
                          {item.createdAt}
                        </td>

                        <td className="py-3 px-4 whitespace-nowrap">
                          {getStatusBadge(item.status)}
                        </td>

                        <td className="py-3 px-4 text-right whitespace-nowrap ignore-excel">
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Super Admin Approvals Action - STRICTLY SUPER ADMIN ONLY */}
                            {effectiveTab === "APPROVALS" && isSuperAdmin && item.status === "PENDING" && (
                              <>
                                <Button
                                  size="sm"
                                  color="success"
                                  variant="flat"
                                  onPress={() => handleApprove(item)}
                                  className="text-xs h-8 px-3 font-bold text-emerald-800 bg-emerald-100 hover:bg-emerald-200"
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  color="danger"
                                  variant="flat"
                                  onPress={() => handleReject(item)}
                                  className="text-xs h-8 px-3 font-bold text-red-800 bg-red-100 hover:bg-red-200"
                                >
                                  Reject
                                </Button>
                              </>
                            )}

                            {/* User Edit if Pending */}
                            {effectiveTab === "MY_LEAVES" && item.status === "PENDING" && (
                              <Button
                                size="sm"
                                variant="light"
                                isIconOnly
                                onPress={() => {
                                  setEditApplicationData(item);
                                  setIsApplyModalOpen(true);
                                }}
                                className="h-8 w-8 text-blue-700 hover:bg-blue-100 rounded-lg"
                                title="Edit Application"
                              >
                                <PencilSquareIcon className="w-4 h-4" />
                              </Button>
                            )}

                            {/* Cancel Button */}
                            {(item.status === "PENDING" || item.status === "APPROVED") && (
                              <Button
                                size="sm"
                                variant="light"
                                isIconOnly
                                onPress={() => handleCancelClick(item)}
                                className="h-8 w-8 text-red-600 hover:bg-red-100 rounded-lg"
                                title="Cancel Application"
                              >
                                <NoSymbolIcon className="w-4 h-4" />
                              </Button>
                            )}

                            {/* Review / Details Modal */}
                            <Button
                              size="sm"
                              variant="flat"
                              onPress={() => {
                                setDetailModalItem(item);
                                setApplicantQuotaInfo(null);
                              }}
                              className="text-xs h-8 px-3 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold rounded-lg"
                            >
                              Details
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {paginator.pageCount > 1 && (
            <div className="p-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600 font-medium">
              <span>
                Page {paginator.currentPage || currentPage} of {paginator.pageCount} ({paginator.itemCount} records)
              </span>
              <div className="flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="flat"
                  isDisabled={currentPage <= 1}
                  onPress={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className="h-8 text-xs font-semibold"
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="flat"
                  isDisabled={currentPage >= paginator.pageCount}
                  onPress={() => setCurrentPage((p) => Math.min(paginator.pageCount, p + 1))}
                  className="h-8 text-xs font-semibold"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Apply / Edit Leave Modal */}
      <ApplyLeaveModal
        isOpen={isApplyModalOpen}
        onClose={() => setIsApplyModalOpen(false)}
        onSuccess={handleRefreshAll}
        initialData={editApplicationData}
        quotaData={quota}
        isSuperAdmin={isSuperAdmin}
      />

      {/* Details & Review Dialog Modal */}
      {detailModalItem && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 bg-gradient-to-r from-primary to-primary2 text-white flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <CalendarDaysIcon className="w-5 h-5 text-blue-200" />
                <h3 className="text-base font-bold">Leave Application #{detailModalItem.id}</h3>
              </div>
              <button
                onClick={() => setDetailModalItem(null)}
                className="p-1 rounded-lg text-blue-200 hover:text-white hover:bg-white/10"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto text-sm flex-1">
              {/* Employee Info if Admin */}
              {isSuperAdmin && (
                <div className="bg-slate-50 p-3.5 rounded-xl border border-slate-200 flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-slate-900 text-base">
                      {detailModalItem._user?.name || detailModalItem.user_data?.name || detailModalItem._user?.username || detailModalItem.user_data?.username || `Employee #${detailModalItem.user}`}
                    </h4>
                    <p className="text-xs text-slate-500 font-medium mt-0.5">
                      {detailModalItem._user?._subbranch?.branch_name || detailModalItem._user?.branch_name || detailModalItem.user_data?.subbranch_data?.branch_name || detailModalItem.user_data?.branch || "General Branch"}
                    </p>
                  </div>
                  <div>{getStatusBadge(detailModalItem.status)}</div>
                </div>
              )}

              {/* Applicant Current Quota for Admin review */}
              {isSuperAdmin && applicantQuotaInfo && (
                <div className="bg-blue-50/70 p-3.5 rounded-xl border border-blue-200/60">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-blue-900 text-xs uppercase tracking-wider">Employee Balances ({selectedFY?.value})</span>
                    {applicantQuotaInfo.isProrated && (
                      <span className="text-xs text-purple-800 font-bold bg-purple-100 px-2 py-0.5 rounded">Prorated</span>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="bg-white p-2 rounded-lg border border-blue-100 shadow-2xs">
                      <span className="text-xs text-slate-500 block">CL</span>
                      <strong className="text-slate-900 text-sm font-extrabold">{applicantQuotaInfo.casualRemaining ?? 0}</strong>
                      <span className="text-[10px] text-slate-400 block">/ {applicantQuotaInfo.casualTotal ?? 0}</span>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-blue-100 shadow-2xs">
                      <span className="text-xs text-slate-500 block">SL</span>
                      <strong className="text-slate-900 text-sm font-extrabold">{applicantQuotaInfo.sickRemaining ?? 0}</strong>
                      <span className="text-[10px] text-slate-400 block">/ {applicantQuotaInfo.sickTotal ?? 0}</span>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-blue-100 shadow-2xs">
                      <span className="text-xs text-slate-500 block">PL</span>
                      <strong className="text-slate-900 text-sm font-extrabold">{applicantQuotaInfo.paidRemaining ?? 0}</strong>
                      <span className="text-[10px] text-slate-400 block">/ {applicantQuotaInfo.paidTotal ?? 0}</span>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-blue-100 shadow-2xs">
                      <span className="text-xs text-slate-500 block">Comp-Off</span>
                      <strong className="text-slate-900 text-sm font-extrabold">{applicantQuotaInfo.compOffRemaining ?? 0}</strong>
                      <span className="text-[10px] text-slate-400 block">/ {applicantQuotaInfo.compOffEarned ?? 0}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-slate-600 font-semibold">Leave Type:</span>
                  {getLeaveTypeBadge(detailModalItem.leaveType)}
                </div>

                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-slate-600 font-semibold">Date Range:</span>
                  <span className="font-bold text-slate-900">
                    {moment(detailModalItem.fromDate).format("DD MMM YYYY")} → {moment(detailModalItem.toDate).format("DD MMM YYYY")}
                  </span>
                </div>

                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-slate-600 font-semibold">Total Days:</span>
                  <span className="font-extrabold text-primary text-base">
                    {detailModalItem.totalDays} {detailModalItem.totalDays === 1 ? "Day" : "Days"}
                    {detailModalItem.isHalfDay && ` (${detailModalItem.halfDaySession === "FIRST_HALF" ? "First Half" : "Second Half"})`}
                  </span>
                </div>

                <div className="border-b border-slate-100 pb-2">
                  <span className="text-slate-600 font-semibold block mb-1">Reason:</span>
                  <p className="p-3 bg-slate-50 rounded-xl text-slate-800 leading-relaxed font-medium">
                    {detailModalItem.reason || "No reason provided"}
                  </p>
                </div>

                {detailModalItem.actionRemarks && (
                  <div className="border-b border-slate-100 pb-2">
                    <span className="text-slate-600 font-semibold block mb-1">Approver Remarks:</span>
                    <p className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 leading-relaxed font-medium">
                      {detailModalItem.actionRemarks}
                    </p>
                  </div>
                )}

                {detailModalItem.attachments && detailModalItem.attachments.length > 0 && (
                  <div>
                    <span className="text-slate-600 font-semibold block mb-1.5">Attachments:</span>
                    <div className="space-y-1.5">
                      {(Array.isArray(detailModalItem.attachments) ? detailModalItem.attachments : [detailModalItem.attachments]).map((url: string, idx: number) => (
                        <a
                          key={idx}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-2 p-2 bg-blue-50 text-blue-800 rounded-lg hover:underline truncate text-xs font-semibold"
                        >
                          <PaperClipIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{url.split("/").pop()}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
              <Button
                size="sm"
                variant="flat"
                onPress={() => setDetailModalItem(null)}
                className="text-xs font-semibold"
              >
                Close
              </Button>

              {isSuperAdmin && detailModalItem.status === "PENDING" && (
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    color="danger"
                    variant="flat"
                    onPress={() => handleReject(detailModalItem)}
                    className="text-xs font-bold"
                  >
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    color="success"
                    onPress={() => handleApprove(detailModalItem)}
                    className="text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700"
                  >
                    Approve Leave
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveReport;
